import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, ShieldCheck, PackageCheck, Percent, LayoutGrid } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { PRODUCTS, CATEGORIES, HERO_BANNERS } from '../data/mock';
import { useCatalog } from '../lib/catalog';

const Home = () => {
  const { products: PRODUCTS, categories: CATEGORIES } = useCatalog();
  const categoryProducts = (catId) => PRODUCTS.filter((p) => p.category === catId).slice(0, 6);

  const featuredCats = ['basket', 'bathroom', 'bowls', 'chopper', 'water-bottle', 'water-jug', 'bucket-mop', 'chilly-cutter'];

  return (
    <div className="bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-r from-emerald-50 via-white to-orange-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-16 grid md:grid-cols-2 gap-6 md:gap-8 items-center">
          <div>
            <span className="inline-block px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] md:text-xs font-semibold mb-3 md:mb-4">
              Ecom Seekho — Wholesale Marketplace
            </span>
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-gray-900 leading-tight">
              Sabse Sasta, <br />
              <span className="text-emerald-700">Sabse Achha</span>
            </h1>
            <p className="mt-3 md:mt-4 text-gray-600 text-sm md:text-lg">
              Wholesale rates on 2000+ household, kitchen & utility products. Order in bulk & save more.
            </p>
            <div className="mt-4 md:mt-6 flex flex-wrap gap-2 md:gap-3">
              <Link to="/shop">
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white md:h-10 md:px-4">
                  Shop Now <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/shop?category=chopper">
                <Button size="sm" variant="outline" className="border-orange-400 text-orange-600 hover:bg-orange-50 md:h-10 md:px-4">
                  Browse Choppers
                </Button>
              </Link>
            </div>
            <div className="mt-5 md:mt-8 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 text-[10px] md:text-xs">
              <div className="flex items-center gap-2 text-gray-700"><Truck className="w-4 h-4 text-emerald-600 flex-shrink-0" /> Pan India Delivery</div>
              <div className="flex items-center gap-2 text-gray-700"><Percent className="w-4 h-4 text-orange-500 flex-shrink-0" /> Tier Discounts</div>
              <div className="flex items-center gap-2 text-gray-700"><ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" /> Trusted by Retailers</div>
              <div className="flex items-center gap-2 text-gray-700"><PackageCheck className="w-4 h-4 text-orange-500 flex-shrink-0" /> Bulk Orders</div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-200/40 to-orange-200/40 rounded-3xl blur-3xl" />
            <div className="relative grid grid-cols-2 gap-2 md:gap-3">
              {PRODUCTS.slice(0, 4).map((p) => (
                <div key={p.id} className="aspect-square bg-white rounded-xl shadow-md overflow-hidden hover:scale-105 transition-transform duration-300">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Categories strip */}
      <section className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        <div className="flex items-end justify-between mb-3 md:mb-4">
          <h2 className="text-lg md:text-xl font-bold text-gray-800">Shop by Category</h2>
          <Link
            to="/shop"
            className="text-xs md:text-sm font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-1"
          >
            View All <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-2 md:gap-3">
          {CATEGORIES.slice(0, 13).map((c) => {
            const sample = PRODUCTS.find((p) => p.category === c.id);
            return (
              <Link
                key={c.id}
                to={`/shop?category=${c.id}`}
                className="bg-white rounded-lg border border-gray-200 hover:border-emerald-400 hover:shadow-md p-2 md:p-3 text-center transition-all"
              >
                <div className="aspect-square rounded-md bg-gray-50 overflow-hidden mb-1.5 md:mb-2">
                  {sample && (
                    <img src={sample.image} alt={c.name} className="w-full h-full object-cover" />
                  )}
                </div>
                <span className="text-[10px] md:text-xs font-medium text-gray-700 line-clamp-1">{c.name}</span>
              </Link>
            );
          })}
          {/* All Products tile */}
          <Link
            to="/shop"
            className="rounded-lg border-2 border-dashed border-emerald-400 bg-emerald-50 hover:bg-emerald-100 p-2 md:p-3 text-center transition-all flex flex-col items-center justify-center"
          >
            <div className="aspect-square w-full rounded-md bg-white flex items-center justify-center mb-1.5 md:mb-2 border border-emerald-200">
              <LayoutGrid className="w-6 h-6 md:w-8 md:h-8 text-emerald-700" />
            </div>
            <span className="text-[10px] md:text-xs font-bold text-emerald-800">All Products</span>
            <span className="text-[9px] md:text-[10px] text-emerald-700">{PRODUCTS.length}+</span>
          </Link>
        </div>
      </section>

      {/* Category product sections */}
      {featuredCats.map((catId) => {
        const cat = CATEGORIES.find((c) => c.id === catId);
        const prods = categoryProducts(catId);
        if (!prods.length) return null;
        return (
          <section key={catId} className="max-w-7xl mx-auto px-4 py-4 md:py-6">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h2 className="text-base md:text-lg font-bold text-gray-800">{cat.name}</h2>
              <Link
                to={`/shop?category=${catId}`}
                className="text-xs md:text-sm text-emerald-700 hover:text-emerald-800 font-medium flex items-center gap-1"
              >
                View all <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
              {prods.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        );
      })}

      {/* Bulk CTA */}
      <section className="max-w-7xl mx-auto px-4 my-12">
        <div className="rounded-2xl bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold">Bulk Order Enquiry</h3>
            <p className="mt-2 text-emerald-100">Customized rates for orders above ₹50,000. Direct-from-factory pricing.</p>
          </div>
          <Link to="/shop">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white">
              Get Quote <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
